import React, { useEffect, useState } from 'react';
import DoctorInfoService from '../doctorInfo/doctorInfoService';
import OfficeService from '../office/officeService';
import imageService from '../images/imageService';
import AuthService from '../Security/AuthService';
import './DoctorDirectory.css';

const DoctorDirectory = () => {
  const [doctors, setDoctors] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const doctorsPerPage = 10;

  const doctorService = new DoctorInfoService();
  const officeService = new OfficeService();
  const authService = new AuthService();

  const [role] = useState(localStorage.getItem('role') || '');

  const isAdminView = role === 'ADMIN' || role === 'MANAGER';

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        /*
          Public users should only get safe public care-team records.

          Admin/Manager users can still get all doctor/staff accounts
          because they need management buttons like reset password/delete.
        */
        const visibleDoctors = isAdminView
          ? await doctorService.getAllDoctors()
          : await doctorService.getPublicCareTeam();

        const withOfficeNamesAndImages = await Promise.all(
          visibleDoctors.map(async (doc) => {
            let officeName = 'Not assigned';

            try {
              if (doc.officeId) {
                officeName = await officeService.getOfficeNameById(doc.officeId);
              }
            } catch (error) {
              console.error(
                `Error loading office name for officeId ${doc.officeId}:`,
                error
              );
            }

            const imageUrl = doc.doctorPicture
              ? imageService.getImageUrl('doctor', doc.doctorPicture)
              : '/images/doctor/default.jpg';

            return {
              ...doc,
              officeName,
              imageUrl,
            };
          })
        );

        setDoctors(withOfficeNamesAndImages);
      } catch (error) {
        console.error('Error loading doctor directory:', error);
      }
    };

    fetchDoctors();
  }, [isAdminView]);

  const filteredDoctors = doctors.filter((doc) => {
    const firstName = doc.firstName || doc.firstname || '';
    const lastName = doc.lastName || doc.lastname || '';
    const specialty = doc.specialty || '';
    const officeName = doc.officeName || '';

    const searchText = search.toLowerCase();

    return (
      firstName.toLowerCase().includes(searchText) ||
      lastName.toLowerCase().includes(searchText) ||
      specialty.toLowerCase().includes(searchText) ||
      officeName.toLowerCase().includes(searchText)
    );
  });

  const indexOfLastDoctor = currentPage * doctorsPerPage;
  const indexOfFirstDoctor = indexOfLastDoctor - doctorsPerPage;

  const currentDoctors = filteredDoctors.slice(
    indexOfFirstDoctor,
    indexOfLastDoctor
  );

  const totalPages = Math.ceil(filteredDoctors.length / doctorsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleImageError = (e) => {
    if (!e.target.dataset.fallback) {
      e.target.src = '/images/doctor/default.jpg';
      e.target.dataset.fallback = 'true';
    }
  };

  const handleResetPassword = async (doc, event) => {
    event.stopPropagation();

    const firstName = doc.firstName || doc.firstname || '';
    const lastNameRaw = doc.lastName || doc.lastname || '';
    const lastName = lastNameRaw.trim().toLowerCase();

    if (!lastName) {
      alert('Cannot reset password: last name is missing for this staff member.');
      return;
    }

    const newPassword = `${lastName}1`;

    const ok = window.confirm(
      `Reset password for ${firstName} ${lastNameRaw}?\n\n` +
        `New temporary password will be: "${newPassword}".\n` +
        `The staff member should change it after logging in.`
    );

    if (!ok) return;

    try {
      /*
        This admin endpoint should return the full doctor record,
        including username, but it should only be accessible to ADMIN/MANAGER.
      */
      const fullDoctor = await doctorService.getDoctorByIdForAdmin(doc.doctorId);

      const username = fullDoctor.username;

      if (!username) {
        alert(
          'Cannot reset password: username is missing for this staff member.'
        );
        return;
      }

      await doctorService.adminResetPassword(doc.doctorId, newPassword);

      await authService.editUser(
        username,
        newPassword,
        fullDoctor.role || 'DOCTOR'
      );

      alert('Password has been reset.');
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Error resetting password. Please check the console or logs.');
    }
  };

  const handleDeleteAccount = async (doc, event) => {
    event.stopPropagation();

    const firstName = doc.firstName || doc.firstname || '';
    const lastName = doc.lastName || doc.lastname || '';

    const ok = window.confirm(
      `Are you sure you want to DELETE this staff account?\n\n` +
        `Name: ${firstName} ${lastName}\n\n` +
        `This action cannot be undone.`
    );

    if (!ok) return;

    try {
      await doctorService.deleteDoctor(doc.doctorId);

      setDoctors((prev) =>
        prev.filter((doctor) => doctor.doctorId !== doc.doctorId)
      );

      alert('Staff account deleted.');
    } catch (error) {
      console.error('Error deleting staff account:', error);
      alert('Error deleting staff account. Please check the console or logs.');
    }
  };

  return (
    <div className="doctor-directory">
      <div className="doctor-description">
        <h1>Meet Our Care Team</h1>

        <p>
          Welcome to our Doctor Directory. Here you’ll find licensed Physical
          Therapists, Massage Therapists, Acupuncture providers, and other PT
          staff members who are here to support your health and recovery. Each
          professional profile includes their specialty, the office they work
          from, and a short biography if available.
        </p>

        <p>
          Use the search bar below to quickly find a therapist by name,
          specialty, or office. You can also browse through the list at your own
          pace. Click on a card to learn more about each provider’s background
          and see which office they are connected to.
        </p>

        <p>
          Please note: choosing a doctor here does not guarantee immediate
          availability. Schedules depend on office hours and how busy a provider
          may be at the time of booking. We’ll always do our best to match you
          with your preferred provider or suggest alternatives if needed.
        </p>

        <p>
          Our goal is to make it easy for you to connect with the right member
          of our team so that your care feels personal, convenient, and
          effective.
        </p>
      </div>

      <input
        type="text"
        placeholder="Search by name, office or specialty"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setCurrentPage(1);
        }}
        className="search-input"
      />

      <div className="doctor-list">
        {currentDoctors.length > 0 ? (
          currentDoctors.map((doc, index) => {
            const firstName = doc.firstName || doc.firstname || '';
            const lastName = doc.lastName || doc.lastname || '';

            return (
              <div
                className="doctor-card"
                key={doc.doctorId || index}
                onClick={() => setSelectedDoctor(doc)}
              >
                <img
                  src={doc.imageUrl}
                  alt={`${firstName} ${lastName}`}
                  onError={handleImageError}
                />

                <h3>
                  {firstName} {lastName}
                </h3>

                <p>
                  <strong>Specialty:</strong>{' '}
                  {doc.specialty || 'Not listed'}
                </p>

                <p>
                  <strong>Office:</strong>{' '}
                  {doc.officeName || 'Not assigned'}
                </p>

                {isAdminView && (
                  <div className="admin-actions">
                    <button
                      className="admin-btn admin-btn-reset"
                      onClick={(e) => handleResetPassword(doc, e)}
                    >
                      Reset Password
                    </button>

                    <button
                      className="admin-btn admin-btn-delete"
                      onClick={(e) => handleDeleteAccount(doc, e)}
                    >
                      Delete Account
                    </button>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <p className="no-results">No care team members found.</p>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Prev
          </button>

          <span>
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {selectedDoctor && (
        <div className="doctor-popup" onClick={() => setSelectedDoctor(null)}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="close-btn"
              onClick={() => setSelectedDoctor(null)}
            >
              &times;
            </button>

            <img
              src={selectedDoctor.imageUrl}
              alt={`${
                selectedDoctor.firstName || selectedDoctor.firstname || ''
              } ${selectedDoctor.lastName || selectedDoctor.lastname || ''}`}
              onError={handleImageError}
            />

            <h2>
              {selectedDoctor.firstName || selectedDoctor.firstname || ''}{' '}
              {selectedDoctor.lastName || selectedDoctor.lastname || ''}
            </h2>

            <p>
              <strong>Specialty:</strong>{' '}
              {selectedDoctor.specialty || 'Not listed'}
            </p>

            <p>
              <strong>Office:</strong>{' '}
              {selectedDoctor.officeName || 'Not assigned'}
            </p>

            <p className="bio">
              {selectedDoctor.bio?.trim()
                ? selectedDoctor.bio
                : 'This therapist has not added a bio yet.'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorDirectory;